import { useMutation, useQuery } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';

// Path helpers
export const bizyChatV2Path = (prefix = '') => `${prefix}/bizy/chat/v2`;
export const bizyFeedbackPath = (messageId: string, prefix = '') => `${prefix}/bizy/feedback/${messageId}`;
export const bizyTitlePath = (prefix = '') => `${prefix}/bizy/title`;

// Streaming API Types
export interface BizyChatV2Request {
	message: string;
	conversation_id?: string;
	context?: Record<string, string>;
}

export interface BizyChatV2Response {
	success: boolean;
	conversation_id: string;
	request_id?: string;
}

/**
 * Hook for sending chat messages to Bizy (streaming).
 *
 * POSTs to /api/v2/bizy/chat/v2 which enqueues a background job and returns
 * 202 immediately. The response is streamed via ActionCable (BizyChannel).
 */
export const useBizyChatV2Mutation = () => {
	const http = useHttpClient();

	return useMutation({
		mutationFn: async (params: BizyChatV2Request): Promise<BizyChatV2Response> => {
			const response = await http.post<BizyChatV2Response>(bizyChatV2Path(), params);
			return response.data;
		},
	});
};

// Cancel API
export const bizyChatCancelPath = (prefix = '') => `${prefix}/bizy/chat/v2/cancel`;

export const useBizyCancelMutation = () => {
	const http = useHttpClient();

	return useMutation({
		mutationFn: async (params: { conversation_id: string }) => {
			const response = await http.post(bizyChatCancelPath(), params);
			return response.data;
		},
	});
};

// Feedback API Types
export interface BizyFeedbackRequest {
	messageId: string;
	feedback_type: 'positive' | 'negative' | null;
	feedback_category?: string;
	feedback_details?: string;
}

export interface BizyFeedbackResponse {
	success: boolean;
	error?: string;
}

/**
 * Hook for submitting feedback on a Bizy response.
 *
 * PATCH /api/v2/bizy/feedback/:id
 */
export const useBizyFeedbackMutation = () => {
	const http = useHttpClient();

	return useMutation({
		mutationFn: async ({ messageId, ...data }: BizyFeedbackRequest): Promise<BizyFeedbackResponse> => {
			const response = await http.patch<BizyFeedbackResponse>(bizyFeedbackPath(messageId), data);
			return response.data;
		},
	});
};

// Title Generation API Types
export interface BizyTitleRequest {
	message: string;
}

export interface BizyTitleResponse {
	title: string;
}

/**
 * Hook for generating an AI conversation title from a user message.
 *
 * POST /api/v2/bizy/title — synchronous, no streaming, no persistence.
 */
export const useBizyTitleMutation = () => {
	const http = useHttpClient();

	return useMutation({
		mutationFn: async (params: BizyTitleRequest): Promise<BizyTitleResponse> => {
			const response = await http.post<BizyTitleResponse>(bizyTitlePath(), params);
			return response.data;
		},
	});
};

// Activation State API
export const bizyActivationStatePath = (prefix = '') => `${prefix}/bizy/activation_state`;

export interface ActivationStateUserSignals {
	recognition_given_ever: boolean;
	recognition_given_last_30_days: boolean;
	recognition_received_ever: boolean;
	recognition_received_last_30_days: boolean;
	has_active_one_on_one: boolean;
	has_completed_one_on_one: boolean;
	completed_one_on_one_last_30_days: boolean;
}

export interface ActivationStateAdminSignals {
	activated: boolean;
	activated_at: string | null;
	company_user_count: number;
	company_active_user_count: number;
	company_recognition_count: number;
	company_one_on_one_count: number;
	completed_one_on_one_count_last_30_days: number;
}

export interface ActivationStateCompanySignals {
	days_since_signup: number | null;
}

export interface ActivationStateResponse {
	user: ActivationStateUserSignals;
	company: ActivationStateCompanySignals;
	admin?: ActivationStateAdminSignals;
}

export const bizyActivationStateQueryKey = () => ['bizy-activation-state'];

export const useBizyActivationState = (enabled = true) => {
	const http = useHttpClient();

	return useQuery({
		queryKey: bizyActivationStateQueryKey(),
		queryFn: async (): Promise<ActivationStateResponse> => {
			const response = await http.get<ActivationStateResponse>(bizyActivationStatePath());
			return response.data;
		},
		enabled,
		staleTime: 5 * 60 * 1000,
	});
};
